import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rolesApi } from '@/api/roles'
import type { AdminRole, AdminRoleRequest } from '@/types/api'
import { ShieldCheck, Plus, Pencil, Trash2, X, Check } from 'lucide-react'
import { toast } from 'sonner'

const ALL_PERMISSIONS = [
  { key: 'DASHBOARD', label: '대시보드' },
  { key: 'USERS', label: '사용자 관리' },
  { key: 'ORGANIZATIONS', label: '기관 관리' },
  { key: 'SUBSCRIPTIONS_ORG', label: '조직 구독 관리' },
  { key: 'SUBSCRIPTIONS_PERSONAL', label: '개인 구독 관리' },
  { key: 'PRODUCTS', label: '상품 관리' },
  { key: 'CONTENTS', label: '콘텐츠 관리' },
  { key: 'CONFIGURATIONS', label: '학습진행 설정' },
  { key: 'LEARNING_STATUS', label: '학습 현황' },
  { key: 'ANNOUNCEMENTS', label: '공지사항 관리' },
  { key: 'SUPPORT_INQUIRIES', label: '문의 관리' },
  { key: 'LIBRARY', label: '자료실' },
  { key: 'NOTES', label: '메모장' },
  { key: 'DATA_STATS', label: 'DATA 확인' },
]

interface RoleFormData {
  name: string
  description: string
  permissions: string[]
}

const emptyForm: RoleFormData = { name: '', description: '', permissions: [] }

export function RoleManagement() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null)
  const [form, setForm] = useState<RoleFormData>(emptyForm)

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: () => rolesApi.getRoles(),
    select: (res) => res.data.data ?? [],
  })

  const roles = rolesData ?? []

  const createMutation = useMutation({
    mutationFn: (data: AdminRoleRequest) => rolesApi.createRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
      toast.success('역할이 생성되었습니다.')
      closeModal()
    },
    onError: () => toast.error('역할 생성에 실패했습니다.'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdminRoleRequest }) => rolesApi.updateRole(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
      toast.success('역할이 수정되었습니다.')
      closeModal()
    },
    onError: () => toast.error('역할 수정에 실패했습니다.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rolesApi.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] })
      toast.success('역할이 삭제되었습니다.')
    },
    onError: () => toast.error('역할 삭제에 실패했습니다. 배정된 사용자가 있는지 확인하세요.'),
  })

  const openCreateModal = () => {
    setEditingRole(null)
    setForm(emptyForm)
    setIsModalOpen(true)
  }

  const openEditModal = (role: AdminRole) => {
    setEditingRole(role)
    setForm({
      name: role.name,
      description: role.description ?? '',
      permissions: [...role.permissions],
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingRole(null)
    setForm(emptyForm)
  }

  const toggleAll = () => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.length === ALL_PERMISSIONS.length
        ? []
        : ALL_PERMISSIONS.map(p => p.key),
    }))
  }

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('역할 이름을 입력하세요.')
      return
    }
    if (form.permissions.length === 0) {
      toast.error('최소 1개 이상의 권한을 선택하세요.')
      return
    }

    const data: AdminRoleRequest = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      permissions: form.permissions,
    }

    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleDelete = (role: AdminRole) => {
    if (!confirm(`"${role.name}" 역할을 삭제하시겠습니까?`)) return
    deleteMutation.mutate(role.id)
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-amber-500" />
          <h1 className="text-2xl font-bold text-slate-900">역할 관리</h1>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          새 역할
        </button>
      </div>

      {/* Role List */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400">로딩 중...</div>
      ) : (
        <div className="grid gap-4">
          {roles.map((role) => (
            <div
              key={role.id}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-slate-900">{role.name}</h3>
                    {role.isSuper && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                        최고 관리자
                      </span>
                    )}
                    <span className="text-sm text-slate-400">
                      {role.userCount}명 배정
                    </span>
                  </div>
                  {role.description && (
                    <p className="mt-1 text-sm text-slate-500">{role.description}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {role.isSuper ? (
                      <span className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded">
                        모든 권한
                      </span>
                    ) : (
                      role.permissions.map((perm) => {
                        const label = ALL_PERMISSIONS.find(p => p.key === perm)?.label ?? perm
                        return (
                          <span key={perm} className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded">
                            {label}
                          </span>
                        )
                      })
                    )}
                  </div>
                </div>
                {!role.isSuper && (
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(role)}
                      className="p-2 text-slate-400 hover:text-blue-500 transition-colors"
                      title="수정"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(role)}
                      className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                      title="삭제"
                      disabled={role.userCount > 0}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">
                {editingRole ? '역할 수정' : '새 역할 생성'}
              </h2>
              <button onClick={closeModal} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">역할 이름</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="예: 콘텐츠 관리자"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">설명</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="역할에 대한 설명 (선택)"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    권한 ({form.permissions.length}/{ALL_PERMISSIONS.length})
                  </label>
                  <button
                    onClick={toggleAll}
                    className="text-xs text-amber-600 hover:text-amber-700"
                  >
                    {form.permissions.length === ALL_PERMISSIONS.length ? '전체 해제' : '전체 선택'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {ALL_PERMISSIONS.map((perm) => (
                    <label
                      key={perm.key}
                      onClick={() => setForm(prev => ({
                        ...prev,
                        permissions: prev.permissions.includes(perm.key)
                          ? prev.permissions.filter(p => p !== perm.key)
                          : [...prev.permissions, perm.key],
                      }))}
                      className={`
                        flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors
                        ${form.permissions.includes(perm.key)
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-slate-200 hover:border-slate-300'
                        }
                      `}
                    >
                      <div className={`
                        w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
                        ${form.permissions.includes(perm.key)
                          ? 'bg-amber-500 border-amber-500'
                          : 'border-slate-300'
                        }
                      `}>
                        {form.permissions.includes(perm.key) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-slate-700">{perm.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
              >
                {isSaving ? '저장 중...' : editingRole ? '수정' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
